import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { Subscription } from 'rxjs';
import { VALID_CEFRS } from 'src/app/shared/constants';
import { UnsavedConfirmationDialog } from 'src/app/shared/components/confirmation/unsaved-confirmation.component';
import {
	CreateDiagnosticQuestion,
	QuizBlockLevel,
	QuizOptionBlock,
	RichContent,
	RichContentBlock,
} from 'src/app/shared/models/diagnosis.model';
import { ImageFiles, IUploadImage } from 'src/app/shared/models/image-upload.model';
import { DiagnosticQuestionsService } from 'src/app/shared/services/diagnostic-questions.service';
import { ImageUploaderService } from 'src/app/shared/services/image-uploader.service';
import { DiagnosticQuestionsStore } from 'src/app/shared/services/stores/diagnostic-questions.store.service';
import { TtsService } from 'src/app/shared/services/tts.service';
import { GOOGLE_VOICES, GoogleVoice, googleVoicePreviewUrl } from 'src/app/shared/models/tts.model';
import { diagnosticImageUrl } from 'src/app/shared/utils/diagnostic-asset.util';

type OptionContentType = 'text' | 'audio' | 'image';

const OPTION_SLOT_COUNT = 4;
const MIN_FILLED_OPTIONS = 2;

interface OptionEditorState {
	text: string;
	audioUrl: string;
	audioTtsText: string;
	audioVoice: GoogleVoice;
	audioTtsOverride: boolean;
	imageId: string;
	imageVerify: string;
	imageToVerify: boolean;
	imageExists: boolean;
	imageFile: ImageFiles | null;
	isCorrect: boolean;
}

@Component({
	selector: 'app-question-editor',
	templateUrl: './question-editor.component.html',
	styleUrls: ['./question-editor.component.scss'],
})
export class QuestionEditorComponent implements OnInit, OnDestroy {
	readonly cefrs = VALID_CEFRS;
	readonly googleVoices = GOOGLE_VOICES;
	readonly optionLetters = ['A', 'B', 'C', 'D'];
	readonly optionSlotCount = OPTION_SLOT_COUNT;

	isUpdate = false;
	questionId: string | null = null;
	loading = true;
	saving = false;
	formDirty = false;
	levelValidationAttempted = false;

	stimulusText = '';
	stimulusAudioUrl = '';
	stimulusAudioTtsText = '';
	stimulusAudioVoice: GoogleVoice = 'Zephyr';
	stimulusAudioTtsOverride = false;
	stimulusImageId = '';
	stimulusImageVerify = '';
	stimulusImageToVerify = false;
	stimulusImageExists = false;
	stimulusImageFile: ImageFiles | null = null;
	generatingStimulusTts = false;

	optionContentType: OptionContentType = 'text';
	multipleCorrectAnswers = false;
	options: OptionEditorState[] = [];
	optionTtsLoading: boolean[] = [];

	metaForm = new FormGroup({
		name: new FormControl('', Validators.required),
		cefr: new FormControl(null, Validators.required),
		published: new FormControl(false),
		instruction: new FormControl('', Validators.required),
	});

	private subs = new Subscription();
	private voicePreviewAudio: HTMLAudioElement | null = null;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private dialog: MatDialog,
		private toastService: ToastService,
		private diagnosticQuestionsService: DiagnosticQuestionsService,
		private diagnosticQuestionsStore: DiagnosticQuestionsStore,
		private imageUploaderService: ImageUploaderService,
		private ttsService: TtsService,
	) {}

	ngOnInit(): void {
		this.initOptions(OPTION_SLOT_COUNT);

		const formSub = this.metaForm.valueChanges.subscribe(() => {
			this.formDirty = true;
		});
		this.subs.add(formSub);

		const id = this.route.snapshot.paramMap.get('questionId');
		if (id) {
			this.isUpdate = true;
			this.questionId = id;
			this.loadQuestion(id);
		} else {
			this.loading = false;
			this.formDirty = false;
		}
	}

	ngOnDestroy(): void {
		this.stopVoicePreview();
		this.subs.unsubscribe();
	}

	playVoicePreview(voice: GoogleVoice): void {
		this.stopVoicePreview();
		const audio = new Audio(googleVoicePreviewUrl(voice));
		this.voicePreviewAudio = audio;
		audio.play().catch(() => {
			this.toastService.warn(`Pré-visualização indisponível para a voz ${voice}`);
		});
		audio.onended = () => {
			if (this.voicePreviewAudio === audio) {
				this.voicePreviewAudio = null;
			}
		};
	}

	private stopVoicePreview(): void {
		if (this.voicePreviewAudio) {
			this.voicePreviewAudio.pause();
			this.voicePreviewAudio.currentTime = 0;
			this.voicePreviewAudio = null;
		}
	}

	private loadQuestion(id: string): void {
		this.diagnosticQuestionsService.getQuestion(id).subscribe({
			next: (q) => {
				this.metaForm.patchValue({
					name: q.name,
					cefr: q.cefr,
					published: q.published,
				});
				if (q.level) {
					this.applyLevelToEditor(q.level);
				}
				this.loading = false;
				this.formDirty = false;
			},
			error: () => {
				this.toastService.error('Falha ao carregar a pergunta');
				this.loading = false;
				this.router.navigate(['/navbar/diagnostic-questions']);
			},
		});
	}

	private applyLevelToEditor(level: QuizBlockLevel): void {
		const questionBlocks = level.question?.blocks ?? [];
		const instructions = questionBlocks.find(b => b.type === 'instructions');
		if (instructions && instructions.type === 'instructions') {
			this.metaForm.patchValue({ instruction: instructions.content });
		}

		this.stimulusText = '';
		this.stimulusAudioUrl = '';
		this.stimulusImageId = '';
		this.stimulusImageVerify = '';
		this.stimulusImageToVerify = false;
		this.stimulusImageExists = false;
		this.stimulusImageFile = null;

		const stimulusBlocks = questionBlocks.filter(b => b.type !== 'instructions');
		stimulusBlocks.forEach(block => {
			if (block.type === 'text') {
				this.stimulusText = block.content;
			} else if (block.type === 'audio') {
				this.stimulusAudioUrl = block.content;
			} else if (block.type === 'image') {
				this.stimulusImageId = this.extractImageIdFromUrl(block.content);
				this.stimulusImageVerify = this.stimulusImageId;
				this.stimulusImageToVerify = true;
				this.stimulusImageExists = true;
			}
		});

		if (level.options?.length) {
			const firstBlock = level.options[0].content?.blocks?.[0];
			if (firstBlock) {
				this.optionContentType = firstBlock.type as OptionContentType;
			}
			this.multipleCorrectAnswers = level.options.filter(o => o.isCorrect).length > 1;
			this.initOptions(OPTION_SLOT_COUNT);
			level.options.forEach((opt, i) => {
				if (!this.options[i]) return;
				this.options[i].isCorrect = opt.isCorrect;
				const c = opt.content?.blocks?.[0];
				if (!c) return;
				if (c.type === 'text') {
					this.options[i].text = c.content;
				} else if (c.type === 'audio') {
					this.options[i].audioUrl = c.content;
				} else if (c.type === 'image') {
					this.options[i].imageId = this.extractImageIdFromUrl(c.content);
					this.options[i].imageVerify = this.options[i].imageId;
					this.options[i].imageToVerify = true;
					this.options[i].imageExists = true;
				}
			});
		}
	}

	private extractImageIdFromUrl(url: string): string {
		if (!url) return '';
		const parts = url.split('/');
		const file = parts[parts.length - 1] || '';
		return file.replace(/\.(jpg|jpeg|png)$/i, '');
	}

	initOptions(count: number): void {
		this.options = Array.from({ length: count }, () => this.createEmptyOption());
		this.optionTtsLoading = Array(count).fill(false);
	}

	createEmptyOption(): OptionEditorState {
		return {
			text: '',
			audioUrl: '',
			audioTtsText: '',
			audioVoice: 'Zephyr',
			audioTtsOverride: false,
			imageId: '',
			imageVerify: '',
			imageToVerify: false,
			imageExists: false,
			imageFile: null,
			isCorrect: false,
		};
	}

	setOptionContentType(type: OptionContentType): void {
		if (this.optionContentType === type) return;
		this.formDirty = true;
		this.optionContentType = type;
		this.options = this.options.map(() => this.createEmptyOption());
	}

	generateStimulusTts(): void {
		const text = this.stimulusAudioTtsText?.trim();
		if (!text) {
			this.toastService.warn('Digite um texto para gerar o áudio');
			return;
		}
		this.generatingStimulusTts = true;
		this.ttsService.generateSpeech({
			text,
			speaker: this.stimulusAudioVoice,
			provider: 'google',
			...(this.stimulusAudioTtsOverride ? { override: true } : {}),
		}).subscribe({
			next: (res) => {
				this.generatingStimulusTts = false;
				if (res.publicUrl) {
					this.stimulusAudioUrl = res.publicUrl;
					this.formDirty = true;
				}
			},
			error: () => {
				this.generatingStimulusTts = false;
				this.toastService.error('Falha ao gerar áudio');
			},
		});
	}

	resetStimulusTts(): void {
		this.stimulusAudioUrl = '';
		this.formDirty = true;
	}

	generateOptionTts(index: number): void {
		const text = this.options[index].audioTtsText?.trim();
		if (!text) {
			this.toastService.warn('Digite um texto para gerar o áudio');
			return;
		}
		this.setOptionTtsLoading(index, true);
		const opt = this.options[index];
		this.ttsService.generateSpeech({
			text,
			speaker: opt.audioVoice,
			provider: 'google',
			...(opt.audioTtsOverride ? { override: true } : {}),
		}).subscribe({
			next: (res) => {
				this.setOptionTtsLoading(index, false);
				if (res.publicUrl) {
					this.options[index].audioUrl = res.publicUrl;
					this.formDirty = true;
				}
			},
			error: () => {
				this.setOptionTtsLoading(index, false);
				this.toastService.error('Falha ao gerar áudio');
			},
		});
	}

	resetOptionTts(index: number): void {
		this.options[index].audioUrl = '';
		this.formDirty = true;
	}

	private setOptionTtsLoading(index: number, loading: boolean): void {
		this.optionTtsLoading = this.optionTtsLoading.map((value, i) => (i === index ? loading : value));
	}

	stimulusImageIdChange(value: string): void {
		this.stimulusImageId = value;
		this.stimulusImageToVerify = false;
		this.stimulusImageExists = false;
		this.stimulusImageFile = null;
	}

	verifyStimulusImage(): void {
		this.stimulusImageVerify = this.stimulusImageId;
		this.stimulusImageToVerify = true;
	}

	onStimulusImageExists(id: string): void {
		this.stimulusImageExists = true;
		this.stimulusImageId = id;
		this.formDirty = true;
	}

	onStimulusImageFile(file: ImageFiles | null): void {
		this.stimulusImageFile = file;
		this.stimulusImageExists = false;
		if (file) {
			this.stimulusImageId = file.name;
		}
		this.formDirty = true;
	}

	optionImageIdChange(index: number, value: string): void {
		this.options[index].imageId = value;
		this.options[index].imageToVerify = false;
		this.options[index].imageExists = false;
		this.options[index].imageFile = null;
	}

	verifyOptionImage(index: number): void {
		this.options[index].imageVerify = this.options[index].imageId;
		this.options[index].imageToVerify = true;
	}

	onOptionImageExists(index: number, id: string): void {
		this.options[index].imageExists = true;
		this.options[index].imageId = id;
		this.formDirty = true;
	}

	onOptionImageFile(index: number, file: ImageFiles | null): void {
		this.options[index].imageFile = file;
		this.options[index].imageExists = false;
		if (file) {
			this.options[index].imageId = file.name;
		}
		this.formDirty = true;
	}

	toggleOptionCorrect(index: number): void {
		if (!this.multipleCorrectAnswers) {
			if (this.options[index].isCorrect) {
				this.options[index].isCorrect = false;
			} else {
				this.options.forEach((o, i) => (o.isCorrect = i === index));
			}
		} else {
			this.options[index].isCorrect = !this.options[index].isCorrect;
		}
		this.formDirty = true;
	}

	clearStimulusText(): void {
		this.stimulusText = '';
		this.formDirty = true;
	}

	clearStimulusImage(): void {
		this.stimulusImageId = '';
		this.stimulusImageVerify = '';
		this.stimulusImageToVerify = false;
		this.stimulusImageExists = false;
		this.stimulusImageFile = null;
		this.formDirty = true;
	}

	clearStimulusAudio(): void {
		this.stimulusAudioUrl = '';
		this.stimulusAudioTtsText = '';
		this.stimulusAudioTtsOverride = false;
		this.formDirty = true;
	}

	clearOption(index: number): void {
		this.options[index] = this.createEmptyOption();
		this.formDirty = true;
	}

	hasStimulusTextContent(): boolean {
		return !!this.stimulusText.trim();
	}

	hasStimulusImageContent(): boolean {
		return !!this.stimulusImageId?.trim() || this.stimulusImageExists || !!this.stimulusImageFile;
	}

	hasStimulusAudioContent(): boolean {
		return !!this.stimulusAudioUrl?.trim() || !!this.stimulusAudioTtsText?.trim();
	}

	hasOptionContent(index: number): boolean {
		const opt = this.options[index];
		return opt.isCorrect || !this.isOptionEmpty(index)
			|| !!opt.audioTtsText?.trim()
			|| !!opt.imageId?.trim();
	}

	onFieldChange(): void {
		this.formDirty = true;
	}

	private hasStimulusText(): boolean {
		return !!this.stimulusText.trim();
	}

	private hasStimulusAudio(): boolean {
		return !!this.stimulusAudioUrl?.trim();
	}

	private hasStimulusImage(): boolean {
		if (!this.stimulusImageId?.trim()) return false;
		return this.stimulusImageExists || !!this.stimulusImageFile;
	}

	private isStimulusImagePartial(): boolean {
		return !!this.stimulusImageId?.trim() && !this.stimulusImageExists && !this.stimulusImageFile;
	}

	isOptionEmpty(index: number): boolean {
		return !this.isOptionFilled(this.options[index]);
	}

	isOptionFilled(opt: OptionEditorState): boolean {
		if (this.optionContentType === 'text') {
			return !!opt.text.trim();
		}
		if (this.optionContentType === 'audio') {
			return !!opt.audioUrl?.trim();
		}
		return !!opt.imageId?.trim() && (opt.imageExists || !!opt.imageFile);
	}

	private isOptionPartial(opt: OptionEditorState): boolean {
		if (this.optionContentType === 'image') {
			return !!opt.imageId?.trim() && !opt.imageExists && !opt.imageFile;
		}
		return false;
	}

	private buildLevel(resolveImageUrls: boolean): QuizBlockLevel | null {
		const instruction = this.metaForm.get('instruction')?.value?.trim() || '';
		const blocks: RichContentBlock[] = [];

		if (instruction) {
			blocks.push({ type: 'instructions', content: instruction });
		}

		if (this.hasStimulusText()) {
			blocks.push({ type: 'text', content: this.stimulusText.trim() });
		}
		if (this.hasStimulusAudio()) {
			blocks.push({ type: 'audio', content: this.stimulusAudioUrl });
		}
		if (this.hasStimulusImage()) {
			const url = this.resolveImageUrl(
				this.stimulusImageId,
				this.stimulusImageExists,
				this.stimulusImageFile,
				resolveImageUrls,
			);
			if (url) {
				blocks.push({ type: 'image', content: url });
			}
		}

		const question: RichContent = { blocks };

		const options: QuizOptionBlock[] = this.options
			.filter(o => this.isOptionFilled(o))
			.map(o => ({
				content: { blocks: [this.buildOptionBlock(o, resolveImageUrls)] },
				isCorrect: o.isCorrect,
			}));

		return { question, options };
	}

	private buildOptionBlock(o: OptionEditorState, resolve: boolean): RichContentBlock {
		if (this.optionContentType === 'text') {
			return { type: 'text', content: o.text.trim() };
		}
		if (this.optionContentType === 'audio') {
			return { type: 'audio', content: o.audioUrl };
		}
		const url = this.resolveImageUrl(o.imageId, o.imageExists, o.imageFile, resolve);
		return { type: 'image', content: url || '' };
	}

	private resolveImageUrl(
		imageId: string,
		exists: boolean,
		file: ImageFiles | null,
		usePendingPreview: boolean,
	): string {
		if (!imageId) return '';
		if (exists) {
			return diagnosticImageUrl(imageId);
		}
		if (usePendingPreview && file?.path) {
			return file.path as string;
		}
		return '';
	}

	private collectPendingImageUploads(): { files: ImageFiles[]; blobs: Blob[]; ids: IUploadImage[] } {
		const files: ImageFiles[] = [];
		const blobs: Blob[] = [];
		const ids: IUploadImage[] = [];

		const add = (f: ImageFiles | null) => {
			if (f?.file) {
				files.push(f);
				blobs.push(new Blob([f.file], { type: 'image/jpeg' }));
				ids.push({ id: f.name, extension: 'jpg' });
			}
		};

		if (this.stimulusImageFile) {
			add(this.stimulusImageFile);
		}
		if (this.optionContentType === 'image') {
			this.options.forEach(o => add(o.imageFile));
		}

		return { files, blobs, ids };
	}

	save(): void {
		if (this.metaForm.invalid) {
			this.metaForm.markAllAsTouched();
			this.toastService.error('Preencha todos os campos obrigatórios');
			return;
		}

		const levelErrors = this.getLevelValidationErrors();
		if (levelErrors.length) {
			this.levelValidationAttempted = true;
			this.toastService.error(levelErrors[0]);
			return;
		}

		this.saving = true;
		const pending = this.collectPendingImageUploads();
		const dto = this.buildSaveDto();

		if (pending.blobs.length > 0) {
			this.imageUploaderService.uploadImages(pending.blobs, pending.ids).subscribe((res) => {
				if (!res) {
					this.toastService.error('Falha no upload de imagens');
					this.saving = false;
					return;
				}
				if (!this.isUpdate) {
					this.createQuestion(dto);
				} else {
					this.updateQuestion(dto);
				}
			});
		} else if (!this.isUpdate) {
			this.createQuestion(dto);
		} else {
			this.updateQuestion(dto);
		}
	}

	private buildSaveDto(): CreateDiagnosticQuestion {
		const formValue = this.metaForm.value;
		return {
			name: formValue.name!,
			cefr: formValue.cefr as CreateDiagnosticQuestion['cefr'],
			level: this.buildLevel(true)!,
			published: formValue.published ?? false,
		};
	}

	private createQuestion(dto: CreateDiagnosticQuestion): void {
		this.diagnosticQuestionsService.createQuestion(dto).subscribe({
			next: (res) => {
				this.saving = false;
				if (!res) {
					this.toastService.error('Falha ao criar a pergunta');
					return;
				}
				this.formDirty = false;
				this.toastService.success('Pergunta criada com sucesso');
				this.diagnosticQuestionsStore.getQuestions(true);
				this.router.navigate(['/navbar/diagnostic-questions']);
			},
			error: (err) => {
				console.error('Error creating question', err);
				this.saving = false;
				this.toastService.error('Falha ao criar a pergunta');
			},
		});
	}

	private updateQuestion(dto: CreateDiagnosticQuestion): void {
		if (!this.questionId) {
			this.saving = false;
			return;
		}
		this.diagnosticQuestionsService.updateQuestion(this.questionId, dto).subscribe({
			next: () => {
				this.saving = false;
				this.formDirty = false;
				this.toastService.success('Pergunta atualizada com sucesso');
				this.diagnosticQuestionsStore.getQuestions(true);
				this.router.navigate(['/navbar/diagnostic-questions']);
			},
			error: (err) => {
				console.error('Error updating question', err);
				this.saving = false;
				this.toastService.error('Falha ao atualizar a pergunta');
			},
		});
	}

	getLevelValidationErrors(): string[] {
		const errors: string[] = [];

		if (this.isStimulusImagePartial()) {
			errors.push('Verifique ou envie a imagem do estímulo');
		} else if (!this.hasStimulusText() && !this.hasStimulusAudio() && !this.hasStimulusImage()) {
			errors.push('Preencha pelo menos um bloco de estímulo (texto, áudio ou imagem)');
		}

		const filledCount = this.options.filter(o => this.isOptionFilled(o)).length;
		if (filledCount < MIN_FILLED_OPTIONS) {
			errors.push(`Preencha pelo menos ${MIN_FILLED_OPTIONS} alternativas`);
		}

		for (let i = 0; i < this.options.length; i++) {
			const optionError = this.getOptionValidationError(i);
			if (optionError) {
				errors.push(optionError);
			}
		}

		const filledOptions = this.options.filter(o => this.isOptionFilled(o));
		if (filledOptions.length > 0 && !filledOptions.some(o => o.isCorrect)) {
			errors.push('Marque pelo menos uma resposta correta');
		}

		return errors;
	}

	showLevelValidation(): boolean {
		return this.levelValidationAttempted;
	}

	isStimulusInvalid(): boolean {
		return this.showLevelValidation()
			&& !this.hasStimulusText()
			&& !this.hasStimulusAudio()
			&& !this.hasStimulusImage();
	}

	isStimulusImageInvalid(): boolean {
		return this.showLevelValidation() && this.isStimulusImagePartial();
	}

	isOptionInvalid(index: number): boolean {
		return this.showLevelValidation() && !!this.getOptionValidationError(index);
	}

	isCorrectAnswerInvalid(): boolean {
		if (!this.showLevelValidation()) return false;
		const filled = this.options.filter(o => this.isOptionFilled(o));
		return filled.length > 0 && !filled.some(o => o.isCorrect);
	}

	getOptionErrorMessage(index: number): string {
		return this.getOptionValidationError(index) || '';
	}

	private getOptionValidationError(index: number): string | null {
		const o = this.options[index];
		const label = this.optionLetters[index] || String(index + 1);

		if (o.isCorrect && !this.isOptionFilled(o)) {
			return `A opção ${label} está marcada como correta mas está vazia`;
		}

		if (this.isOptionPartial(o)) {
			return `Verifique ou envie a imagem da opção ${label}`;
		}

		return null;
	}

	cancel(): void {
		if (this.formDirty) {
			const dialogRef = this.dialog.open(UnsavedConfirmationDialog, { width: '400px' });
			dialogRef.afterClosed().subscribe(confirmed => {
				if (confirmed) {
					this.router.navigate(['/navbar/diagnostic-questions']);
				}
			});
		} else {
			this.router.navigate(['/navbar/diagnostic-questions']);
		}
	}
}
